import { useEffect, useState } from "react";
import { fetchWeatherForLocation } from "../services/weatherService";
import { getWeatherInfo } from "../utils/getWeatherEmoji"; // Updated import
import { WeatherData } from "../types/weather-types";

// Define a function to determine if a territory uses Fahrenheit
export const isFahrenheitRegion = (latitude: number, longitude: number) => {
  // A basic mapping of known lat/lon ranges for Fahrenheit regions
  // (you can expand this logic based on your needs)
  const fahrenheitRegions = [
    { latRange: [24.396308, 49.384358], lonRange: [-125.0, -66.93457] }, // Contiguous US
    // Add more known ranges or specific points for territories that use Fahrenheit
  ];

  return fahrenheitRegions.some(({ latRange, lonRange }) => {
    return (
      latitude >= latRange[0] &&
      latitude <= latRange[1] &&
      longitude >= lonRange[0] &&
      longitude <= lonRange[1]
    );
  });
};

export const useWeather = (
  latitude: number | null | undefined,
  longitude: number | null | undefined,
  isNight: boolean
) => {
  const [weather, setWeather] = useState<string | null>(null);
  const [emoji, setEmoji] = useState<string | null>(null);
  const [description, setDescription] = useState<string | null>(null);
  const [advice, setAdvice] = useState<string | null>(null);

  useEffect(() => {
    const getWeather = async () => {
      if (latitude && longitude) {
        try {
          const weatherData: WeatherData = await fetchWeatherForLocation(
            latitude,
            longitude
          );

          if (!weatherData || !weatherData.hourly) {
            console.error("Invalid weather data received:", weatherData);
            setWeather("No weather data available");
            return;
          }

          const currentWeatherCode = weatherData.hourly.weathercode?.[0];
          const currentTemperature = weatherData.hourly.temperature_2m?.[0];

          if (
            currentWeatherCode !== undefined &&
            currentTemperature !== undefined
          ) {
            const usesFahrenheit = isFahrenheitRegion(latitude, longitude);
            let temperature: number;
            let temperatureUnit: string;

            if (usesFahrenheit) {
              temperature = (currentTemperature * 9) / 5 + 32; // Convert to Fahrenheit
              temperatureUnit = "°F";
            } else {
              temperature = currentTemperature; // Use Celsius
              temperatureUnit = "°C";
            }

            setWeather(`${temperature.toFixed(1)}${temperatureUnit}`);
            const { emoji, description } = getWeatherInfo(
              currentWeatherCode,
              isNight
            );
            setEmoji(emoji);
            setDescription(description);
            if ([61, 63, 65, 66, 67, 51, 53, 55].includes(currentWeatherCode)) {
              setAdvice(
                "Looks like it's raining today. You may want to bring an umbrella today!"
              );
            } else {
              setAdvice(null); // Clear advice for non-rainy weather
            }
          } else {
            setWeather("No valid weather data available");
          }
        } catch (error) {
          console.error("Failed to fetch weather:", error);
          setWeather("Unable to fetch weather");
        }
      } else {
        console.warn(
          "Invalid latitude or longitude provided:",
          latitude,
          longitude
        );
        setWeather("Location unavailable");
      }
    };

    getWeather();
  }, [latitude, longitude, isNight]);

  return { weather, emoji, description, advice };
};
